import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Base/Select";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  subWeeks,
  subMonths,
  subQuarters,
  startOfYear,
  endOfYear,
  subYears,
  format,
} from "date-fns";

export type DateRange = {
  label: string;
  start: Date;
  end: Date;
};

export type DateRangeOption = {
  value: string;
  label: string;
  getRange: () => DateRange;
};

const getDateRangeOptions = (): DateRangeOption[] => {
  const now = new Date();

  return [
    {
      value: "last7days",
      label: "Last 7 Days",
      getRange: () => ({
        label: "Last 7 Days",
        start: new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000),
        end: now,
      }),
    },
    {
      value: "thisWeek",
      label: "This Week",
      getRange: () => ({
        label: "This Week",
        start: startOfWeek(now, { weekStartsOn: 1 }), // Monday start
        end: endOfWeek(now, { weekStartsOn: 1 }),
      }),
    },
    {
      value: "lastWeek",
      label: "Last Week",
      getRange: () => {
        const lastWeek = subWeeks(now, 1);
        return {
          label: "Last Week",
          start: startOfWeek(lastWeek, { weekStartsOn: 1 }),
          end: endOfWeek(lastWeek, { weekStartsOn: 1 }),
        };
      },
    },
    {
      value: "thisMonth",
      label: "This Month",
      getRange: () => ({
        label: "This Month",
        start: startOfMonth(now),
        end: endOfMonth(now),
      }),
    },
    {
      value: "lastMonth",
      label: "Last Month",
      getRange: () => {
        const lastMonth = subMonths(now, 1);
        return {
          label: "Last Month",
          start: startOfMonth(lastMonth),
          end: endOfMonth(lastMonth),
        };
      },
    },
    {
      value: "thisQuarter",
      label: "This Quarter",
      getRange: () => ({
        label: "This Quarter",
        start: startOfQuarter(now),
        end: endOfQuarter(now),
      }),
    },
    {
      value: "lastQuarter",
      label: "Last Quarter",
      getRange: () => {
        const lastQuarter = subQuarters(now, 1);
        return {
          label: "Last Quarter",
          start: startOfQuarter(lastQuarter),
          end: endOfQuarter(lastQuarter),
        };
      },
    },
    {
      value: "thisYear",
      label: "This Year",
      getRange: () => ({
        label: "This Year",
        start: startOfYear(now),
        end: endOfYear(now),
      }),
    },
    {
      value: "lastYear",
      label: "Last Year",
      getRange: () => {
        const lastYear = subYears(now, 1);
        return {
          label: "Last Year",
          start: startOfYear(lastYear),
          end: endOfYear(lastYear),
        };
      },
    },
  ];
};

interface DateRangeSelectorProps {
  selectedRange: DateRange;
  onRangeChange: (range: DateRange) => void;
  isLoading?: boolean;
}

export const DateRangeSelector = ({
  selectedRange,
  onRangeChange,
  isLoading = false,
}: DateRangeSelectorProps) => {
  const options = getDateRangeOptions();

  const handleSelect = (value: string) => {
    const option = options.find((opt) => opt.value === value);
    if (option) {
      onRangeChange(option.getRange());
    }
  };

  const getCurrentOptionValue = (): string => {
    const option = options.find((opt) => {
      const range = opt.getRange();
      return (
        range.start.getTime() === selectedRange.start.getTime() &&
        range.end.getTime() === selectedRange.end.getTime()
      );
    });
    return option?.value || "last7days";
  };

  return (
    <div className="flex items-center gap-2">
      <Select
        value={getCurrentOptionValue()}
        onValueChange={handleSelect}
        disabled={isLoading}
      >
        <SelectTrigger className="w-48">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <div className="flex items-center gap-2 text-sm text-gray-500">
        {format(selectedRange.start, "MMM dd")} -{" "}
        {format(selectedRange.end, "MMM dd, yyyy")}
        {isLoading && (
          <div className="flex items-center gap-1 text-blue-600">
            <div className="h-3 w-3 animate-spin rounded-full border-2 border-blue-600 border-t-transparent"></div>
            <span className="text-xs">Loading...</span>
          </div>
        )}
      </div>
    </div>
  );
};
